
import type { ApiError, Product, ProductService, Promotion } from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { PromotionDomainService } from '../../services'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { EpbText, HorizontalResultCard, Skeleton } from '../shared'
import ToolingPreviewNoResults from '../tooling/tooling-preview-no-results.component.vue'

export default defineComponent({
  name: 'PromotionProductListPreview',
  components: {
    Skeleton,
    HorizontalResultCard,
    EpbText,
    ToolingPreviewNoResults,
  },
  props: {
    promotion: {
      type: Object as PropType<Promotion>,
      required: true,
    },
  },
  setup(props, ctx: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const messages = $epb.messages.tools.promotions.preview

    const promotionDomainService = CONTAINER.get<PromotionDomainService>(
      TYPES.PromotionDomainService,
    )

    const items = ref<Product[]>([])
    const itemsLoading = ref<boolean>(true)

    const header = computed<{ title?: string; subtitle?: string }>(() => {
      return {
        title: promotionDomainService.getTitle(props.promotion.setting),
        subtitle: promotionDomainService.getSubtitle(props.promotion.setting),
      }
    })

    async function fetchPreviewItems(): Promise<void> {
      if (!props.promotion) {
        return
      }

      try {
        items.value = []
        itemsLoading.value = true

        const productService = CONTAINER.get<ProductService>(TYPES.ProductService)

        items.value = await productService.fetchItemsByKeyword(
          promotionDomainService.getProductPreviewFilters?.(props.promotion.setting),
        )
      } catch (error) {
        if (error.status) {
          ctx.emit('showError', $epb.utils.decodeErrorMessage(error as ApiError))
        }
        console.error(error)
      } finally {
        itemsLoading.value = false
      }
    }

    watch(() => props.promotion.setting.config, fetchPreviewItems, { immediate: true, deep: true })

    return {
      baseClass: 'promotion-product-list-preview',
      messages,
      header,
      items,
      itemsLoading,
    }
  },
})
